import { useEffect, useMemo, useRef, useState } from "react";
import { ImArrowLeft, ImArrowRight } from "react-icons/im";
import { IoClose } from "react-icons/io5";
import { FaRegFilePdf } from "react-icons/fa6";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { formatDate, getRealFileName, getUploadUrl } from "src/Common/common";

const AttachmentImageSlider = ({ shown, attachments = [], activeIndex = 0, onCancel }) => {
  const [selectedIndex, setSelectedIndex] = useState(activeIndex);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragAnchor = useRef({ x: 0, y: 0 });
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  useEffect(() => {
    if (shown) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "auto";
      };
    }

    document.body.style.overflow = "auto";
  }, [shown]);

  useEffect(() => {
    setSelectedIndex(activeIndex);
  }, [activeIndex]);

  useEffect(() => {
    // Reset zoom/pan when changing slide.
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setIsDragging(false);
  }, [selectedIndex, shown]);

  const total = attachments.length;
  const selectedAttachment = attachments[selectedIndex];
  const selectedFileName = selectedAttachment?.fileName || "";
  const selectedSrc = selectedFileName ? getUploadUrl(selectedFileName) : "";

  const hasMultiple = total > 1;

  const handlePrevious = () => {
    if (!hasMultiple) return;
    setSelectedIndex((prev) => (prev === 0 ? total - 1 : prev - 1));
  };

  const handleNext = () => {
    if (!hasMultiple) return;
    setSelectedIndex((prev) => (prev === total - 1 ? 0 : prev + 1));
  };

  const handleZoomByWheel = (event) => {
    if (!selectedAttachment || selectedAttachment.isPdf) return;
    event.preventDefault();

    const currentZoom = zoom;
    const minZoom = 1;
    const maxZoom = 4;
    const zoomStep = 0.2;
    const direction = event.deltaY > 0 ? -1 : 1;
    const nextZoom = Math.max(minZoom, Math.min(maxZoom, currentZoom + direction * zoomStep));
    if (nextZoom === currentZoom) return;

    const container = event.currentTarget;
    const rect = container.getBoundingClientRect();
    const cursor = {
      x: event.clientX - rect.left - rect.width / 2,
      y: event.clientY - rect.top - rect.height / 2,
    };
    const ratio = nextZoom / currentZoom;

    setOffset((prev) => ({
      x: cursor.x - (cursor.x - prev.x) * ratio,
      y: cursor.y - (cursor.y - prev.y) * ratio,
    }));
    setZoom(nextZoom);
  };

  const handleMouseDown = (event) => {
    if (zoom <= 1 || selectedAttachment?.isPdf) return;
    event.preventDefault();
    setIsDragging(true);
    dragAnchor.current = {
      x: event.clientX - offset.x,
      y: event.clientY - offset.y,
    };
  };

  const handleMouseMove = (event) => {
    if (!isDragging) return;
    setOffset({
      x: event.clientX - dragAnchor.current.x,
      y: event.clientY - dragAnchor.current.y,
    });
  };

  const stopDragging = () => {
    if (!isDragging) return;
    setIsDragging(false);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleWindowMouseUp = () => setIsDragging(false);
    window.addEventListener("mouseup", handleWindowMouseUp);
    return () => window.removeEventListener("mouseup", handleWindowMouseUp);
  }, [isDragging]);

  useEffect(() => {
    if (zoom <= 1 && (offset.x !== 0 || offset.y !== 0)) {
      setOffset({ x: 0, y: 0 });
    }
  }, [zoom, offset.x, offset.y]);

  useEffect(() => {
    if (!shown) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onCancel?.();
      } else if (event.key === "ArrowLeft") {
        handlePrevious();
      } else if (event.key === "ArrowRight") {
        handleNext();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [shown, onCancel, hasMultiple, total]);

  const selectedDisplayName = useMemo(() => {
    if (!selectedAttachment) return "";
    return selectedAttachment.displayName || getRealFileName(selectedFileName) || selectedFileName || "Attachment";
  }, [selectedAttachment, selectedFileName]);

  if (!shown || !selectedAttachment) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/70"
      onClick={onCancel}
    >
      <div
        className="relative w-full h-full flex flex-col items-center gap-3 px-4 pt-12 pb-3"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="absolute top-3 right-4 z-10 w-9 h-9 rounded-full bg-black/80 text-white flex items-center justify-center hover:bg-black/95 transition-colors"
          onClick={onCancel}
        >
          <IoClose size={22} />
        </button>

        <div className="relative w-full flex-1 min-h-0 flex items-center justify-center">
          {hasMultiple && (
            <button
              type="button"
              className="absolute left-2 md:left-4 z-10 w-9 h-9 rounded-full bg-black/80 text-white flex items-center justify-center hover:bg-black/95 transition-colors"
              onClick={handlePrevious}
            >
              <ImArrowLeft size={14} />
            </button>
          )}

          <div
            className="w-full h-full overflow-hidden flex items-center justify-center"
            onWheel={handleZoomByWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={stopDragging}
            onMouseLeave={stopDragging}
          >
            {selectedAttachment.isPdf ? (
              <div className="w-full h-full bg-white p-2 md:p-3">
                {selectedSrc ? (
                  <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                    <Viewer fileUrl={selectedSrc} plugins={[defaultLayoutPluginInstance]} />
                  </Worker>
                ) : (
                  <div className="w-full h-full bg-[#d9dde5] flex flex-col items-center justify-center gap-3 px-4 text-center">
                    <FaRegFilePdf size={64} className="text-subTitleColor" />
                    <span className="font-OpenSansRegular text-[16px] text-titleColor break-all">{selectedDisplayName}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full h-full bg-transparent flex items-center justify-center overflow-hidden">
                <img
                  src={selectedSrc}
                  alt={selectedDisplayName}
                  className="max-w-full max-h-full select-none"
                  draggable={false}
                  onDragStart={(event) => event.preventDefault()}
                  style={{
                    transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                    transformOrigin: "center center",
                    transition: isDragging ? "none" : "transform 120ms ease-out",
                    cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
                  }}
                />
              </div>
            )}
          </div>

          {hasMultiple && (
            <button
              type="button"
              className="absolute right-2 md:right-4 z-10 w-9 h-9 rounded-full bg-black/80 text-white flex items-center justify-center hover:bg-black/95 transition-colors"
              onClick={handleNext}
            >
              <ImArrowRight size={14} />
            </button>
          )}
        </div>

        <div className="w-full flex flex-col gap-2">
          <div className="w-full flex items-center justify-center rounded-[8px] bg-black/75 px-3 py-2 text-white gap-5">
            <p className={`font-OpenSansRegular text-[13px] ${selectedAttachment.description ? "" : "text-white/80"}`}>
              {selectedAttachment.description || "No description"}
            </p>
            <div className=" flex flex-wrap items-center gap-2">
              {selectedAttachment.tags?.length ? (
                selectedAttachment.tags.map((tag) => (
                  <span
                    key={`${selectedAttachment.id}-${tag.id}`}
                    className="font-OpenSansRegular text-[11px] bg-black/60 border border-white/20 rounded-[999px] px-2 py-[2px]"
                  >
                    {tag.name}
                  </span>
                ))
              ) : (
                <span className="font-OpenSansRegular text-[12px] text-white/80">No tag</span>
              )}
            </div>
            <div className=" flex flex-wrap items-center gap-x-4 gap-y-1 font-OpenSansRegular text-[12px] text-white/85">
              <span>{selectedAttachment.createdBy || "-"}</span>
              <span>{selectedAttachment.createdAt ? formatDate(selectedAttachment.createdAt) : "-"}</span>
            </div>
          </div>

          <div className="w-full flex items-center justify-center gap-3 overflow-x-auto pb-1">
            {attachments.map((attachment, index) => {
              const isActive = selectedIndex === index;
              const fileName = attachment.fileName || "";
              const thumbSrc = fileName ? getUploadUrl(fileName) : "";
              const thumbLabel = attachment.displayName || getRealFileName(fileName) || fileName || "Attachment";

              return (
                <button
                  key={`${attachment.id}-${index}`}
                  type="button"
                  className={`w-[62px] h-[62px] shrink-0 rounded-[4px] overflow-hidden border-[2px] ${isActive ? "border-white" : "border-transparent opacity-80"
                    }`}
                  onClick={() => setSelectedIndex(index)}
                >
                  {attachment.isPdf ? (
                    <div className="w-full h-full bg-[#c7ccd6] flex items-center justify-center">
                      <FaRegFilePdf size={26} className="text-subTitleColor" />
                    </div>
                  ) : (
                    <img src={thumbSrc} alt={thumbLabel} className="w-full h-full object-cover" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachmentImageSlider;
